import { PageOptionSelector } from "@/ExportableFile/Models/PageOptionSelector";
import { FormNavigation } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormNavigation";
import { FormPage } from "@/Patient/Components/MedicalNote/ObstetricalFile/Navigation/FormPage";
import { FormExtensibleComponents } from "@/Patient/Models/Forms/FormExtendableComponentsEnum";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";
import { QuebecObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFileForm";
import { QuebecObstetricalFilePageIdEnum } from "@/Patient/Models/ObstetricalFile/Quebec/QuebecObstetricalFilePageIdEnum";
import { IObstetricalFileProps, ObstetricalFile } from "../ObstetricalFile";
import { QuebecObstetricalFilePageFour } from "../Quebec/Page/PageFour/QuebecObstetricalFilePageFour";
import { QuebecObstetricalFilePageOne } from "../Quebec/Page/PageOne/QuebecObstetricalFilePageOne";
import { QuebecObstetricalFilePageThree } from "../Quebec/Page/PageThree/QuebecObstetricalFilePageThree";
import { QuebecObstetricalFilePageTwo } from "../Quebec/Page/PageTwo/QuebecObstetricalFilePageTwo";

class QuebecObstetricalFile extends ObstetricalFile<QuebecObstetricalFileForm> {
    protected obstetricalFileTypeCssClass: string = "quebec";
    private readonly componentId = "quebec-obstetrical-file";

    protected getComponentId(): string {
        return this.componentId;
    }

    protected loadFormNavigation(props: IObstetricalFileProps): FormNavigation {
        const pageOne = new FormPage({ id: QuebecObstetricalFilePageIdEnum.Page1.toString(), name: `${cacheStore.GetString("PageLabel")} 1`, render: () => this.renderPageOne() });
        const pageTwo = new FormPage({ id: QuebecObstetricalFilePageIdEnum.Page2.toString(), name: `${cacheStore.GetString("PageLabel")} 2`, render: () => this.renderPageTwo() });
        const pageThree = new FormPage({ id: QuebecObstetricalFilePageIdEnum.Page3.toString(), name: `${cacheStore.GetString("PageLabel")} 3`, render: () => this.renderPageThree() });
        const pageFour = new FormPage({ id: QuebecObstetricalFilePageIdEnum.Page4.toString(), name: `${cacheStore.GetString("PageLabel")} 4`, render: () => this.renderPageFour() });

        return new FormNavigation([pageOne, pageTwo, pageThree, pageFour], props.formDisplay.selectedPageId);
    }

    protected getSelectionPage(): PageOptionSelector[] {
        return [
            new PageOptionSelector({
                label: `${cacheStore.GetString("PageLabel")} 1`,
                value: 1,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("PageLabel")} 2`,
                value: 2,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("PageLabel")} 3`,
                value: 3,
                checked: true
            }),
            new PageOptionSelector({
                label: `${cacheStore.GetString("PageLabel")} 4`,
                value: 4,
                checked: true
            })
        ];
    }

    protected getPageNumbers(): number[] {
        return [1, 2, 3, 4];
    }

    private renderPageOne(): JSX.Element {
        const headerSectionName = FormExtensibleComponents.Page1Header;

        return (
            <QuebecObstetricalFilePageOne
                {...this.props}
                form={this.state.form}
                contributionType={this.props.model.ContributionType}
                isPageHeaderExtended={this.props.formDisplay.isSectionExtended(headerSectionName)}
                readonly={this.props.isReadOnly}
                onHeaderToggle={() => this.toggleSection(headerSectionName)}
                onFormUpdate={(form: QuebecObstetricalFileForm) => super.handleFormUpdate(form)}
                onObstetricalCountersUpdate={(form: QuebecObstetricalFileForm) => super.handleObstetricalCountersUpdate(form)}
                previousObstetricalFile={this.props.previousObstetricalFile}
                onPageUnmount={() => this.onPageUnmount()}
            />
        );
    }

    private renderPageTwo(): JSX.Element {
        const headerSectionName = FormExtensibleComponents.Page2Header;

        return (
            <QuebecObstetricalFilePageTwo
                {...this.props}
                form={this.state.form}
                contributionCreationDate={this.props.model.ContributionCreationDate}
                isPageHeaderExtended={this.props.formDisplay.isSectionExtended(headerSectionName)}
                readonly={this.props.isReadOnly}
                onHeaderToggle={() => this.toggleSection(headerSectionName)}
                onFormUpdate={(form: QuebecObstetricalFileForm) => super.handleFormUpdate(form)}
                onObstetricalFileVitalSignLink={(form: QuebecObstetricalFileForm, emrTemplateId: string) => super.handleObstetricalFileVitalSignLink(form, emrTemplateId)}
                onMedicalItemVitalSignUpdate={async (vitalSign: ObstetricalFileVitalSign, emrTemplateId: string) => await super.handleVitalSignUpdate(vitalSign, emrTemplateId)}
                onPageUnmount={() => this.onPageUnmount()}
            />
        );
    }

    private renderPageThree(): JSX.Element {
        const headerSectionName = FormExtensibleComponents.Page3Header;
        return (
            <QuebecObstetricalFilePageThree
                {...this.props}
                form={this.state.form}
                isPageHeaderExtended={this.props.formDisplay.isSectionExtended(headerSectionName)}
                readonly={this.props.isReadOnly}
                onHeaderToggle={() => this.toggleSection(headerSectionName)}
                onFormUpdate={(form: QuebecObstetricalFileForm) => super.handleFormUpdate(form)}
                onPageUnmount={() => this.onPageUnmount()}
            />
        );
    }

    private renderPageFour(): JSX.Element {
        const headerSectionName = FormExtensibleComponents.Page4Header;
        return (
            <QuebecObstetricalFilePageFour
                {...this.props}
                form={this.state.form}
                contributionCreationDate={this.props.model.ContributionCreationDate}
                isPageHeaderExtended={this.props.formDisplay.isSectionExtended(headerSectionName)}
                readonly={this.props.isReadOnly}
                onHeaderToggle={() => this.toggleSection(headerSectionName)}
                onFormUpdate={(form: QuebecObstetricalFileForm) => super.handleFormUpdate(form)}
                onObstetricalCountersUpdate={(form: QuebecObstetricalFileForm) => super.handleObstetricalCountersUpdate(form)}
                onObstetricalFileVitalSignLink={(form: QuebecObstetricalFileForm, emrTemplateId: string) => super.handleObstetricalFileVitalSignLink(form, emrTemplateId)}
                onMedicalItemVitalSignUpdate={async (vitalSign: ObstetricalFileVitalSign, emrTemplateId: string) => await super.handleVitalSignUpdate(vitalSign, emrTemplateId)}
                onPageUnmount={() => this.onPageUnmount()}
            />
        );
    }

    private toggleSection(sectionName: FormExtensibleComponents): void {
        this.props.onFormDisplayChange(this.props.formDisplay.toggleSection(sectionName));
    }
}

export { QuebecObstetricalFile };
